import React, { useContext, useState, useEffect, useCallback } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { getUserInfo, getUserList, getMenuList, getUserRoleId } from "redux/selectors";
import { CalendarContext } from ".";
import { CALL_FEATCH_API } from "common/API";
import { Modal, Spin, message, Button, Checkbox } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  EditOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import styled, { keyframes } from "styled-components";
import TabsList from "molecules/TabsList";
import ReusableCalendar from "./ReusableCalendar";
import { StyledDateCell } from "./StyledComponents";
import { dateInDetail } from "utils/Date";
import CreateEventModal from "organisms/CreateEventModal";
import { ReactComponent as Editicon } from "../../assets/Edit_Pencil_01.svg";
import { ReactComponent as TrashDelete } from "../../assets/Trash_Full.svg";
import LeaveWeekCalendar from "pages/WeekCalendarView/EventWeekCalendar";

// ─── Styled Components ────────────────────────────────────────────────────────

const ViewToggleRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; padding: 10px 0 12px;
  .nav-group { display: flex; gap: 4px;
    .nav-btn { width: 32px; height: 32px; border: 1px solid #e0e0e0; border-radius: 6px; background: #e8f9f3; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 13px; color: #333; &:hover { background: #c6f4e7; } }
  }
  .range-title { font-size: 14px; font-weight: 600; color: #333; }
  .view-toggle { display: flex; border: 1px solid #d9d9d9; border-radius: 6px; overflow: hidden;
    button { padding: 5px 14px; border: none; background: #fff; font-size: 13px; cursor: pointer; color: #555; transition: all 0.15s;
      &.active { background: #222; color: #fff; font-weight: 500; }
      &:not(.active):hover { background: #f5f5f5; }
    }
  }
`;

const DetailLabel = styled.span`
  width: 160px;
  flex-shrink: 0;
  color: #888;
  font-size: 13px;
  padding-top: 1px;
`;

const DetailValue = styled.span`
  color: #111;
  font-size: 13.5px;
  font-weight: 450;
  line-height: 1.65;
  a { color: #1890ff; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 0;

  &.no-border {
    border-bottom: none;
  }

  &.section-title {
    padding-top: 18px;
    border-bottom: 1px solid #e0e0e0;
    
  }
`;

const EventPill = styled.div`
  background: #FFEDD4; border-left: 3px solid #FF9300; border-radius: 3px;
  padding: 2px 6px; font-size: 14px; color: #1F243B; margin-bottom: 2px;
  cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  transition: background 0.15s;
  &:hover { background: #fff176; }
  .pill-title { font-size: 14px; font-weight: 500; }
  .pill-time { font-size: 12px; opacity: 0.85; }
`;

const StyleddeactivateModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
    padding: 35px 0px 20px 0px;
  }
  .ant-modal-content {
    box-shadow: 0px 0px 8px #00000029;
    border-radius: 10px;
  }
  .ant-modal-title {
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    color: #111827;
  }
  .ant-modal-body { padding: 0px 24px 28px 24px; }
  .ant-modal-close-x { display: none; }
  .ant-modal-footer { display: none; }
`;

const CloneBtn = styled(Button)`
  && {
    height: 38px;
    padding: 0 28px;
    border-radius: 10px;
    background: #00685f;
    border: none;
    color: #ffffff !important;
    font-weight: 600;
    box-shadow: none;
    &:hover, &:focus { background: #7de8d1 !important; color: #111827 !important; }
  }
`;

const CancelBtn = styled(Button)`
  && {
    height: 38px;
    padding: 0 24px;
    border-radius: 10px;
    border: 1px solid #000000;
    color: #000000;
    font-weight: 500;
    box-shadow: none;
    &:hover { border-color: #9ca3af; color: #111827; }
  }
`;
// Add this styled component near your other styled components at the top
const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #00DA88 !important;
    border-color: #00DA88 !important;
  }
  .ant-checkbox-checked::after {
    border-color: #00DA88 !important;
  }
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: #00DA88 !important;
  }
  .ant-checkbox-inner::after {
    border-color: #111 !important; /* ✅ checkmark color — dark for contrast */
  }
`;

// ─── Shared action-icon styles (matches Myleavereport.js / TimeReport.js treatment) ──

const EDIT_ICON_STYLE = {
  color: "#00685F",
  cursor: "pointer",
  background: "#deefee",
  padding: "2px",
  borderRadius: "4px",
  height: "25px",
  width: "25px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
};

const DELETE_ICON_STYLE = {
  color: "#00685F",
  cursor: "pointer",
  background: "#deefee",
  padding: "2px",
  borderRadius: "4px",
  height: "25px",
  width: "25px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  marginRight: "18px",
};

// ─── Constants ────────────────────────────────────────────────────────────────

const TODAY = moment().format("YYYY-MM-DD");

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(dt) {
  if (!dt) return "---";
  const m = moment.utc(dt);
  if (!m.isValid()) return "---";
  return m.format("Do MMM YYYY; hh:mm A");
}

function eventMatchesDate(evt, cellDate) {
  const cellStr = moment(cellDate).format("YYYY-MM-DD");
  const startStr = moment.utc(evt.startdatetime || evt.start_datetime).format("YYYY-MM-DD");
  const endStr = moment.utc(evt.enddatetime || evt.end_datetime).format("YYYY-MM-DD");
  return cellStr >= startStr && cellStr <= endStr;
}

function buildEditData(evt, updatedBy = "") {
  const calendarstatus = evt.allcalendarstatus === true ? true : false;

  return {
    eventid: evt.eventid,
    eventname: evt.eventname || "",
    matterid: evt.matterid || "",
    mattername: evt.mattername || "",
    startdatetime: moment.utc(
      evt.startdatetime || evt.start_datetime
    ).format("YYYY-MM-DD HH:mm"),
    enddatetime: moment.utc(
      evt.enddatetime || evt.end_datetime
    ).format("YYYY-MM-DD HH:mm"),
    location: evt.location || "",
    meetinglink: evt.meetinglink || "",
    attendees: Array.isArray(evt.attendees) ? evt.attendees : [],
    clients: Array.isArray(evt.clients) ? evt.clients : [],
    responsiblepersons: Array.isArray(evt.responsiblepersons) ? evt.responsiblepersons : [],
    description: evt.description || "",
    sendremaindercount: evt.sendremindercount || evt.sendremaindercount || "",
    sendremaindertype: evt.sendremindertype || evt.sendremaindertype || "",
    calendarstatus,
    updatedby: updatedBy,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

function EventCalendar({ userInfo, menuList, userRoleId, userList, tabId, setTabId, calendarType }) {
  const { tabBarExtraContent, setRefetchEvents, eventAllPermission, eventMinePermission, calendarView, setCalendarView } = useContext(CalendarContext);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("week"); // default week
  const [currentDate, setCurrentDate] = useState(moment());

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editVisible, setEditVisible] = useState(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sendCancellationEmail, setSendCancellationEmail] = useState(false);

  const deleteEventIdRef = React.useRef(null);

  const MineTabID = tabId === "Mine";

  // Derive the permission for the currently active tab
  const activePermission = MineTabID ? eventMinePermission : eventAllPermission;

  function resolveNames(emailsOrIds, userList) {
    if (!Array.isArray(emailsOrIds) || !emailsOrIds.length) return "---";
    return emailsOrIds
      .map((val) => {
        const user = userList?.find(
          (u) => u?.email === val || u?.userid === val || u?.emailid === val
        );
        return user?.fullname || user?.firstname || val;
      })
      .join(", ");
  }

  const fetchEvents = useCallback(async () => {
    if (MineTabID && !userInfo?.userid) return;
    try {
      setLoading(true);
      setEvents([]); // clear stale events before fetching
      const url = MineTabID
        ? `insyts-calendar/getcalendarevents?loginuserid=${userInfo.userid}`
        : `insyts-calendar/getcalendarevents`;
      const res = await CALL_FEATCH_API(url, "get");
      if (res?.status === "SUCCESS" || res?.status === 200) {
        setEvents(res?.response?.data || res?.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  }, [tabId, userInfo?.userid]);

  // Register fetchEvents into context so the create button in tabBarExtraContent can call it
  useEffect(() => {
    if (setRefetchEvents) setRefetchEvents(() => fetchEvents);
  }, [fetchEvents, setRefetchEvents]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Refetch when an event is created/updated from outside this page
  // (e.g. the header's global "+ Create Event" modal).
  useEffect(() => {
    window.addEventListener("calendar-events-updated", fetchEvents);
    return () => window.removeEventListener("calendar-events-updated", fetchEvents);
  }, [fetchEvents]);

  const handleEventClick = async (evtId) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setSelectedEvent(null);
      deleteEventIdRef.current = evtId;
      const res = await CALL_FEATCH_API(
        "insyts-calendar/getallevents",
        "post",
        { eventid: evtId }
      );
      if (res?.status === "SUCCESS" || res?.status === 200) {
        const raw = res?.response?.data ?? res?.data;
        const evt = Array.isArray(raw) ? raw[0] : raw;
        setSelectedEvent(evt);
      } else {
        message.error("Failed to load event details");
        setDetailOpen(false);
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open confirmation modal and reset the checkbox each time
  const handleDeleteClick = () => {
    setSendCancellationEmail(false);
    setDeleteConfirmOpen(true);
  };

  // Actual delete — only called after user confirms
  const handleDeleteConfirm = async () => {
    const eventIdToDelete = deleteEventIdRef.current || selectedEvent?.eventid;
    if (!eventIdToDelete) {
      message.error("No event selected for deletion");
      return;
    }

    try {
      setDeleteLoading(true);

      console.log(userInfo, "userInfo")

      const payload = {
        eventid: String(eventIdToDelete),
        deletedby: userInfo?.userid || userInfo?.userid || "",
        deletedbyname: userInfo?.name || userInfo?.firstname || "",
        ...(sendCancellationEmail ? { sendcancellationemail: true } : { sendcancellationemail: false }),
      };

      const response = await CALL_FEATCH_API(
        "insyts-calendar/deleteevents",
        "delete",
        payload
      );

      if (
        response?.status === "SUCCESS" ||
        response?.status === 200 ||
        response?.statusCode === 200
      ) {
        message.success("Event deleted successfully");
        deleteEventIdRef.current = null;
        setSelectedEvent(null);
        setSendCancellationEmail(false);
        setDeleteConfirmOpen(false);
        setDetailOpen(false);
        fetchEvents();
      } else {
        message.error(response?.message || "Failed to delete event");
      }
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Something went wrong while deleting");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditClick = () => {
    setDetailOpen(false);
    setEditVisible(true);
  };

  // ── Month view cell render ──────────────────────────────────────────────────
  const dateFullCellRender = (cellDate) => {
    const { date: day } = dateInDetail(cellDate);
    const cellStr = moment(cellDate).format("YYYY-MM-DD");
    const dayEvents = events.filter((evt) => eventMatchesDate(evt, cellDate));

    return (
      <StyledDateCell>
        <span className="day">{day}</span>
        <div className="ant-picker-calendar-date-content">
          {dayEvents.map((evt) => {
            const startStr = moment.utc(evt.startdatetime || evt.start_datetime).format("YYYY-MM-DD");
            const endStr = moment.utc(evt.enddatetime || evt.end_datetime).format("YYYY-MM-DD");
            const isStart = cellStr === startStr;
            const isEnd = cellStr === endStr;
            const isSingleDay = startStr === endStr;

            return (
              <EventPill
                key={evt.eventid || evt.id}
                onClick={() => handleEventClick(evt.eventid || evt.id)}
                style={{
                  borderLeft: isStart || isSingleDay ? "3px solid #FF9300" : "3px solid transparent",
                  borderRadius: isSingleDay
                    ? "3px 0 0 3px"
                    : isStart
                      ? "3px 0 0 3px"
                      : isEnd
                        ? "0 3px 3px 0"
                        : "0",
                }}
              >
                <div className="pill-title">{evt.eventname || evt.title}</div>
                <div className="pill-time">
                  {moment.utc(evt.startdatetime || evt.start_datetime).format("hh:mm A")} –{" "}
                  {moment.utc(evt.enddatetime || evt.end_datetime).format("hh:mm A")}
                </div>
              </EventPill>
            );
          })}
        </div>
      </StyledDateCell>
    );
  };

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const allItems = [
    { id: "Mine", label: "Mine", component: <></>, permission: eventMinePermission },
    { id: "All", label: "All", component: <></>, permission: eventAllPermission },
  ];

  // Only show tabs the user has view access to
  const items = allItems.filter((item) => item.permission?.view !== false);

  const handleTabSelect = (key) => {
    const activeTab = items.find((item) => item.id === key);
    if (activeTab) setTabId(activeTab.label);
  };

  return (
    <>
      <TabsList
        items={items}
        handleTabSelect={handleTabSelect}
        tabBarExtraContent={tabBarExtraContent}
        tabId={tabId}
        defaultActiveKey="Mine"
      />

      {/* Week view — FullCalendar via LeaveWeekCalendar with calendar events merged in */}
      {viewMode === "week" && (
        <LeaveWeekCalendar
          externalEvents={events}
          onExternalEventClick={handleEventClick}
          calendarType={calendarType}
          tabId={tabId}
          viewType={calendarView}
          setViewType={setCalendarView}
        />
      )}

      {/* Month view — custom Ant calendar */}
      {viewMode === "month" && (
        <Spin spinning={loading}>
          <ReusableCalendar
            dateFullCellRender={dateFullCellRender}
            isCalendarLoading={loading}
            calendarTitle="Calendar"
            showHoliday={false}
          />
        </Spin>
      )}

      {/* ── Event Detail Modal ─────────────────────────────────────────────── */}
      {detailOpen && (
        <Modal
          open={detailOpen}
          onCancel={() => setDetailOpen(false)}
          footer={null}
          width={620}
          title={
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 8 }}>
              <span style={{ fontSize: "17px", fontWeight: 700, color: "#111" }}>Event Details</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {selectedEvent?.createdby === userInfo?.userid && activePermission?.edit && (  // ✅ only show for creator with edit permission — styled to match Leave Report
                  <EditOutlined
                    onClick={handleEditClick}
                    style={EDIT_ICON_STYLE}
                  />
                )}
                {selectedEvent?.createdby === userInfo?.userid && activePermission?.delete && (  // ✅ only show for creator with delete permission — styled to match Leave Report
                  <DeleteOutlined
                    onClick={handleDeleteClick}
                    style={DELETE_ICON_STYLE}
                  />
                )}
              </div>
            </div>
          }
          closable={true}
          destroyOnClose
        >
          {detailLoading ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}>
              <Spin size="large" />
            </div>
          ) : selectedEvent ? (
            <>
              {/* ── Created by banner ── */}
              {selectedEvent.createdby && (
                <div style={{
                  fontSize: 12,
                  color: "#888",
                  background: "#fafafa",
                  borderRadius: 6,
                  padding: "6px 12px",
                  marginBottom: 12,
                  borderBottom: "1px solid #f0f0f0",
                }}>
                  <b>Created by:</b>{" "}
                  {(() => {
                    const name = resolveNames([selectedEvent.createdby], userList);
                    const date = selectedEvent.createdat
                      ? moment(selectedEvent.createdat).format("Do MMM, YYYY")
                      : "";
                    return `${name}${date ? "; " + date : ""}`;
                  })()}
                </div>
              )}
              <DetailRow>
                <DetailLabel>Event Name</DetailLabel>
                <DetailValue>{selectedEvent.eventname || "---"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Matter</DetailLabel>
                <DetailValue>{selectedEvent.mattername || "---"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Start Date &amp; Time</DetailLabel>
                <DetailValue>{formatDateTime(selectedEvent.startdatetime || selectedEvent.start_datetime)}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>End Date &amp; Time</DetailLabel>
                <DetailValue>{formatDateTime(selectedEvent.enddatetime || selectedEvent.end_datetime)}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Location</DetailLabel>
                <DetailValue>{selectedEvent.location || "---"}</DetailValue>
              </DetailRow>
              <DetailRow className="section-title">
                <DetailLabel>Meeting Link</DetailLabel>
                <DetailValue>
                  {selectedEvent.meetinglink
                    ? <a href={selectedEvent.meetinglink} target="_blank" rel="noreferrer">{selectedEvent.meetinglink}</a>
                    : "---"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel><b>Attendees</b></DetailLabel>
                <DetailValue />
              </DetailRow>
              <DetailRow>
                <DetailLabel>Client</DetailLabel>
                <DetailValue>
                  {Array.isArray(selectedEvent.clientnames) && selectedEvent.clientnames.length
                    ? selectedEvent.clientnames.join(", ")
                    : selectedEvent.clientpersonname || "---"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Responsible Personnel</DetailLabel>
                <DetailValue>
                  {Array.isArray(selectedEvent.responsiblepersonelname) && selectedEvent.responsiblepersonelname.length
                    ? selectedEvent.responsiblepersonelname.join(", ")
                    : selectedEvent.responsiblepersonelname || "---"}
                </DetailValue>
              </DetailRow>
              <DetailRow className="section-title">
                <DetailLabel>Employees</DetailLabel>
                <DetailValue>
                  {resolveNames(selectedEvent.attendees, userList)}
                </DetailValue>
              </DetailRow>
              {selectedEvent.description && (
                <DetailRow style={{ flexDirection: "column", gap: 4 }}>
                  <DetailLabel><b>Description</b></DetailLabel>
                  <DetailValue style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>
                    {selectedEvent.description}
                  </DetailValue>
                </DetailRow>
              )}
              {(
                <DetailRow>
                  <DetailLabel>Reminder</DetailLabel>
                  {selectedEvent.sendremaindercount?.length > 0 ? (
                    <DetailValue>
                      {(() => {
                        const count = Number(
                          selectedEvent.sendremindercount || selectedEvent.sendremaindercount || 0
                        );
                        const type = selectedEvent.sendremindertype || selectedEvent.sendremaindertype || "";
                        const singularMap = {
                          minutes: "Minute",
                          hours: "Hour",
                          days: "Day",
                          weeks: "Week",
                        };
                        const label = count === 1
                          ? singularMap[type?.toLowerCase()] || type
                          : (singularMap[type?.toLowerCase()]
                            ? singularMap[type?.toLowerCase()] + "s"
                            : type);
                        return `Before ${count} ${label}`;
                      })()}
                    </DetailValue>
                  ) : "-"}
                </DetailRow>
              )}
            </>
          ) : null}
        </Modal>
      )}

      {/* ── Delete Confirmation Modal ──────────────────────────────────────── */}
      <StyleddeactivateModal
        open={deleteConfirmOpen}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setSendCancellationEmail(false);
        }}
        footer={null}
        width={500}
        closable={false}
        centered
        destroyOnClose
      >
        <div style={{ textAlign: "left", padding: "24px 16px 8px" }}>
          <p style={{ marginBottom: 20, fontSize: 14, color: "#111827" }}>
            Are you sure you want to delete this event?
          </p>

          {/* Cancellation email checkbox */}
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 28,
            }}
          >
            <StyledCheckbox
              checked={sendCancellationEmail}
              onChange={(e) => setSendCancellationEmail(e.target.checked)}
            >
              <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>
                Please send a cancellation email to all attendees
              </span>
            </StyledCheckbox>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
            <CancelBtn
              onClick={() => {
                setDeleteConfirmOpen(false);
                setSendCancellationEmail(false);
              }}
              style={{ width: 150, borderRadius: 10 }}
            >
              No
            </CancelBtn>
            <CloneBtn
              onClick={handleDeleteConfirm}
              loading={deleteLoading}
              style={{ width: 150 }}
            >
              Yes
            </CloneBtn>
          </div>
        </div>
      </StyleddeactivateModal>

      {/* ── Edit Modal ──────────────────────────────────────────────────────── */}
      {editVisible && selectedEvent && (
        <CreateEventModal
          visible={editVisible}
          onClose={(shouldRefresh) => {
            setEditVisible(false);
            if (shouldRefresh) fetchEvents();
          }}
          editData={buildEditData(selectedEvent, userInfo?.userid || "")}
          isEditMode={true}
        />
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  userInfo: getUserInfo(state),
  userList: getUserList(state),
  menuList: getMenuList(state),
  userRoleId: getUserRoleId(state),
});

export default connect(mapStateToProps, null)(EventCalendar);